import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useMutation, useQuery } from '@apollo/client';

import { Box } from '@mui/material';
import { ToastContainer, toast } from 'react-toastify';
import * as Q from '../../../../constants/graphqlQueries';

import CustomTextField from '../../../components/Textfield/CustomTextField';
import ControlledTreeView from '../../../components/Treeview/ControlledTreeviewWithButton';
import CustomButtons from '../../../components/Button/CustomButtons';
import { chunkArrayInGroups, getDictionaryFromArray, getDictionaryFromArrayAsync } from '../../../../utils/arrays';
import transactionResponse from '../../../../constants/transactionResponse';

import './ClientManagement.css'

const CPPSTitle = 'Client Selection';
const transactionMessages = { moduleName: 'Client Management' }

const ClientManagement = () => {
    const user = useSelector((state) => state.User);

    const [employeeId] = useState(String(user.EmployeeId));

    const toastId = useRef();
    const { data: dataClients } = useQuery(Q.APP_ALERT_CLIENTS_ALIAS_GL_QUERY);
    const { data: dataParentOrganizations } = useQuery(Q.APP_ALERT_PARENT_ORGANIZATIONS_FORMATTED_GL_QUERY);
    const {
        data: dataClientParentAccess, 
        refetch: refetchClientParentAccess, 
        loading: dataClientParentAccessIsLoading 
    } = useQuery(Q.ADMINISTRATION_SEARCH_CLIENT_PARENT_ACCESS);
    const [addClientParentAccess, { data: dataAddClientParentAccess, loading: addAccessIsLoading }] = useMutation(Q.ADMINISTRATION_CREATE_CLIENT_PARENT_ACCESS);
    const [removeClientParentAccess, { data: dataRemoveClientParentAccess, loading: removeAccessIsLoading }] = useMutation(Q.ADMINISTRATION_DELETE_CLEINT_PARENT_ACCESS);

    const [search, setSearch] = useState({
        selectedClients: '',
        searchClients: '',
        timerSelectedClients: {},
        timerSearchClients: {},
        searchClientsFilterTerm: '',
        selectedClientsFilterTerm: ''
    });

    const [parentWithChildrenInput, setParentWithChildrenInput] = useState({
        searchClients: [],
        selectedClients: []
    });

    const [searchClientsOnMount, setSearchClientsOnMount] = useState(true);
    const [selectedClientsOnMount, setSelectedClientsOnMount] = useState(true);
    const [refetchClientsOnMount, setRefetchClientsOnMount] = useState(false);

    const [parentsClientsIndexed, setParentsClientsIndexed] = useState({ Parents: {}, Clients: {} });
    const [parentClientState, setParentClientState] = useState({
        searchClientsParentsWithChildrenInput: [],
        mappedParentsWithChildrenInput: [],
        clientsToRemove: [],
        selectedClientsForMappedTreeview: []
    });
    const [clientSelectedIds, setClientSelectedIds] = useState([]);
    const [selectedClient, setSelectedClient] = useState(false);
    const [cleanNodes, setCleanNodes] = useState(false);

  // On mount, getParentOrganizations gets executed
    useEffect(() => {
        if (dataClients) {
            if (dataClients.getClients) {
                const groupsOfClients = chunkArrayInGroups(dataClients.getClients, 100);
                const indexedClientsList = [];
                groupsOfClients.map(group => indexedClientsList.push(getDictionaryFromArrayAsync(group, 'ChildrenId')));
                let clientsIndexed = {};
                Promise.all(indexedClientsList).then(groupsIndexed => {
                    groupsIndexed.map(groupIndexed => {
                    clientsIndexed = { ...clientsIndexed, ...groupIndexed }
                    return null;
                });

                    setParentsClientsIndexed({ ...parentsClientsIndexed, Clients: clientsIndexed });
                });
            }
        }
    }, [dataClients]);
  // On mount, getParentOrganizations gets executed
    useEffect(() => {
        if (dataParentOrganizations) {
            if (dataParentOrganizations.getParentOrganizations) {
                const parentsByKey = getDictionaryFromArray(dataParentOrganizations.getParentOrganizations, 'ParentId');

                setParentsClientsIndexed({
                    ...parentsClientsIndexed, Parents: parentsByKey
                });
            }
        }
    }, [dataParentOrganizations]);

    // If response from adding Client Parent access
    useEffect(() => {
        if (dataAddClientParentAccess) {
            if (dataAddClientParentAccess.createClientParentAccess) {
                if (dataAddClientParentAccess.createClientParentAccess.duplicated === 0) {
                    setRefetchClientsOnMount(true);
                    toast.dismiss(toastId.current);
                    toastId.current = toast(transactionResponse(transactionMessages.moduleName, dataAddClientParentAccess.createClientParentAccess.status));
                }
                else {
                    toast.dismiss(toastId.current);
                    toastId.current = toast(dataAddClientParentAccess.createClientParentAccess.status);
                }
            }
            refetchClientParentAccess();
        }
    }, [dataAddClientParentAccess]);

    // If response from removing Client Parent access
    useEffect(() => {
        if (dataRemoveClientParentAccess) {
            if (dataRemoveClientParentAccess.deleteClientParentAccess) {
                setRefetchClientsOnMount(true);
                toast.dismiss(toastId.current);
                toastId.current = toast(transactionResponse(transactionMessages.moduleName, dataRemoveClientParentAccess.deleteClientParentAccess.status));

                const parentsOnMapped = parentClientState.mappedParentsWithChildrenInput.map(item => ({ ...item }));
                const parentIds = parentsOnMapped.map(parent => parent.ParentId);
                const childrenIds = [];

                parentsOnMapped.forEach((parent) => {
                    if (parent && parent.children && Array.isArray(parent.children)) {
                        parent.children.forEach(child => {
                            if (child && child.ChildrenId) {
                                childrenIds.push(child.ChildrenId);
                            }
                        });
                    }
                });
                refetchClientParentAccess();

                setParentClientState({ ...parentClientState, selectedClientsForMappedTreeview: [...parentIds, ...childrenIds], clientsToRemove: [] });
            }
        }
    }, [dataRemoveClientParentAccess]);

    // If employee has mapped access already, fill the state with the data
    useEffect(() => {
        if (dataClientParentAccess) {
            if (dataClientParentAccess.getClientParentAccess && Object.keys(parentsClientsIndexed.Clients).length && Object.keys(parentsClientsIndexed.Parents).length) {
                const clients = [];

                dataClientParentAccess.getClientParentAccess.forEach((access) => {
                    if (access && access.ClientId && parentsClientsIndexed.Clients[access.ClientId]) {
                        clients.push({ ...parentsClientsIndexed.Clients[access.ClientId] });
                    }
                });

                const clientIdsSelected = clients.map(item => item.ChildrenId);
                const idsSelected = [...clientIdsSelected, ... [...new Set(clients.map(item => item.ParentId))]];
                const parentsSelectedRemovedDuplicatesIds = [...new Set(clients.map(item => item.ParentId))];
                const parentsSelectedRemovedDuplicates = parentsSelectedRemovedDuplicatesIds.map(parentId => parentsClientsIndexed.Parents[parentId]);
                const parentsSelectedId = parentsSelectedRemovedDuplicates.map((parent) => {
                    const parentUpdated = { ...parent }
                    parentUpdated.children = parent?.children.filter(child => clientIdsSelected.includes(child.ChildrenId));

                    return parentUpdated;
                });

                let replaceInputSelection = dataParentOrganizations.getParentOrganizations.map(parent => ({ ...parent }));
                replaceInputSelection = replaceInputSelection.map((parent) => {
                    const parentUpdated = { ...parent }
                    parentUpdated.children = parent.children.filter(child => !(clientIdsSelected.includes(child.ChildrenId)));

                    return parentUpdated;
                }).filter(parent => parent.children.length > 0);

                setParentClientState({ 
                    ...parentClientState, 
                    mappedParentsWithChildrenInput: parentsSelectedId, 
                    selectedClientsForMappedTreeview: idsSelected, 
                    searchClientsParentsWithChildrenInput: replaceInputSelection 
                });
                setRefetchClientsOnMount(false);
            }
        }
    }, [dataClientParentAccess, parentsClientsIndexed]);

    // If changes in the source input, apply to treeview data
    useEffect(() => {
        if (parentClientState.mappedParentsWithChildrenInput.length || parentClientState.searchClientsParentsWithChildrenInput.length) {
            const updatedMapped = parentClientState.mappedParentsWithChildrenInput;
            const updatedSearchClients = parentClientState.searchClientsParentsWithChildrenInput;

            setParentWithChildrenInput({ 
                ...parentWithChildrenInput, 
                selectedClients: updatedMapped, 
                searchClients: updatedSearchClients 
            });
        }
    }, [parentClientState.mappedParentsWithChildrenInput, parentClientState.searchClientsParentsWithChildrenInput]);

    // If search, apply filter to treeview data
    useEffect(() => {
        const timer = setTimeout(() => {
            if (search.searchClients.trim() !== '') {
                const filteredSearchClients = parentWithChildrenInput.searchClients.filter((parent) => {
                    if (!parent || !parent.ParentLabel) return false;
                    
                    let result = parent.ParentLabel.toLowerCase().startsWith(search.searchClients);

                    if (!result && parent.children && Array.isArray(parent.children)) {
                        parent.children.forEach((child) => {
                            if (child && child.ChildrenLabel && child.ChildrenLabel.toLowerCase().startsWith(search.searchClients)) {
                                result = true;
                            }
                        });
                    }

                    return result;
                });

                setParentWithChildrenInput({ ...parentWithChildrenInput, searchClients: filteredSearchClients });
            }
            else {
                setParentWithChildrenInput({ ...parentWithChildrenInput, searchClients: parentClientState.searchClientsParentsWithChildrenInput });
            }
        }, 500);

        setSearch({ ...search, timerSearchClients: timer });
    }, [search.searchClientsFilterTerm]);

    useEffect(() => {
        if (parentWithChildrenInput.searchClients.length) {
            return setSearchClientsOnMount(false);
        }

        return setSelectedClientsOnMount(true);
    }, [parentWithChildrenInput.searchClients]);

    useEffect(() => {
        if (parentWithChildrenInput.selectedClients.length || parentWithChildrenInput.selectedClients !== undefined) {
            return setSelectedClientsOnMount(false);
        }

        return setSelectedClientsOnMount(true);
    }, [parentWithChildrenInput.selectedClients]);

    // If search, apply filter to treeview data
    useEffect(() => {
        const timer = setTimeout(() => {
            if (search.selectedClients.trim() !== '') {
                const filteredSearchClients = parentWithChildrenInput.selectedClients.filter((parent) => {
                    if (!parent || !parent.ParentLabel) return false;
                    
                    let result = parent.ParentLabel.toLowerCase().startsWith(search.selectedClients);

                    if (!result && parent.children) {
                        parent.children.forEach((child) => {
                            if (child && child.ChildrenLabel && child.ChildrenLabel.toLowerCase().startsWith(search.selectedClients)) {
                                result = true;
                            }
                        });
                    }

                    return result;
                });

                setParentWithChildrenInput({ ...parentWithChildrenInput, selectedClients: filteredSearchClients });
            }
            else {
                setParentWithChildrenInput({ ...parentWithChildrenInput, selectedClients: parentClientState.mappedParentsWithChildrenInput });
            }
        }, 500);

        setSearch({ ...search, timerSelectedClients: timer });
    }, [search.selectedClientsFilterTerm]);

    useEffect(() => {
        clearTimeout(search.timerSearchClients);
        setSearch({ ...search, searchClientsFilterTerm: search.searchClients });
    }, [search.searchClients]);

    useEffect(() => {
        clearTimeout(search.timerSelectedClients);
        setSearch({ ...search, selectedClientsFilterTerm: search.selectedClients });
    }, [search.selectedClients]);

    const getUpdatedStateOnParentSelection = (parentClientId) => {
        const selectedParent = { ...parentsClientsIndexed.Parents[parentClientId] }
        const filteredInput = parentClientState.searchClientsParentsWithChildrenInput.filter(parent => parent.ParentId !== parentClientId);
        const clients = selectedParent && selectedParent.children && Array.isArray(selectedParent.children) 
            ? selectedParent.children.map((child) => child && child.ChildrenId).filter(Boolean)
            : [];
        const parentClientsIds = [...clients, parentClientId];

        const replacementParentChildrenInput = filteredInput;
        let replacementPreviewMappedParentsClients = [selectedParent];

        if ('mappedParentsWithChildrenInput' in parentClientState) {
            if (!(parentClientState.mappedParentsWithChildrenInput.filter(item => item.ParentId === parentClientId).length)) {
                replacementPreviewMappedParentsClients = [...parentClientState.mappedParentsWithChildrenInput, selectedParent];
            }
            else {
                const indexForParent = parentClientState.mappedParentsWithChildrenInput.findIndex(item => item.ParentId === parentClientId);
                replacementPreviewMappedParentsClients = parentClientState.mappedParentsWithChildrenInput.map(item => item);
                const foundParent = parentClientState.searchClientsParentsWithChildrenInput.find(parent => parent.ParentId === parentClientId);
                const parentInput = foundParent ? { ...foundParent } : { children: [] };
                
                if (parentInput.children && Array.isArray(parentInput.children) && 
                    replacementPreviewMappedParentsClients[indexForParent] && 
                    replacementPreviewMappedParentsClients[indexForParent].children) {
                    parentInput.children.forEach(item => {
                        if (item) {
                            replacementPreviewMappedParentsClients[indexForParent].children.push(item);
                        }
                    });
                }
            }
        }

        const replacementeSelectedClientsForPreview = [...new Set([...parentClientState.selectedClientsForMappedTreeview, ...parentClientsIds])];

        return {
            searchClientsParentsWithChildrenInput: replacementParentChildrenInput,
            mappedParentsWithChildrenInput: replacementPreviewMappedParentsClients,
            selectedClientsForMappedTreeview: replacementeSelectedClientsForPreview
        }
    }

    const getUpdatedStateOnParentRemoval = (parentOrClientId) => {
        const parentObj = parentsClientsIndexed.Parents[parentOrClientId];
        const clients = parentObj && parentObj.children && Array.isArray(parentObj.children) 
            ? parentObj.children.map((child) => child && child.ChildrenId).filter(Boolean)
            : [];
        const parentClient = { ...parentClientState.mappedParentsWithChildrenInput.find(parentPreview => parentPreview.ParentId === parentOrClientId) }
        let parentChildrenInputSorted = parentClientState.searchClientsParentsWithChildrenInput.map(item => item);

        if (!(parentChildrenInputSorted.filter(item => item.ParentId === parentClient.ParentId).length)) {
            parentChildrenInputSorted.push(parentClient);
        }
        else {
            const indexForParent = parentChildrenInputSorted.findIndex(item => item.ParentId === parentClient.ParentId);
            if (parentClient && parentClient.children && Array.isArray(parentClient.children)) {
                parentClient.children.forEach(item => {
                    if (item && parentChildrenInputSorted[indexForParent] && 
                        parentChildrenInputSorted[indexForParent].children && 
                        Array.isArray(parentChildrenInputSorted[indexForParent].children)) {
                        parentChildrenInputSorted[indexForParent].children.push(item);
                    }
                });
            }
        }

        parentChildrenInputSorted = parentChildrenInputSorted.sort((a, b) => a.ParentLabel.localeCompare(b.ParentLabel, 'en', { numeric: true }));
        const filteredClientsPreview = [...parentClientState.selectedClientsForMappedTreeview].filter(id => id !== parentOrClientId);
        const filteredSelectedClients = [...clients];

        const replacementParentChildrenInput = parentChildrenInputSorted;
        const replacementPreviewMappedParentsClients = parentClientState.mappedParentsWithChildrenInput.filter(parentPreview => parentPreview.ParentId !== parentOrClientId);
        const replacementSelectedClients = filteredSelectedClients;
        const replacementeSelectedClientsForPreview = filteredClientsPreview;

        return {
            searchClientsParentsWithChildrenInput: replacementParentChildrenInput,
            mappedParentsWithChildrenInput: replacementPreviewMappedParentsClients,
            clientsToRemove: replacementSelectedClients,
            selectedClientsForMappedTreeview: replacementeSelectedClientsForPreview
        }
    }

    const handleSearchClientsSearch = (searchInput) => {
        clearTimeout(search.timerSearchClients);
        return setSearch({ ...search, searchClients: searchInput.toLowerCase() });
    }

    const handleMappedSearch = (searchInput) => {
        clearTimeout(search.timerSelectedClients);
        return setSearch({ ...search, selectedClients: searchInput.toLowerCase() });
    }

    const handleClear = () => {
        setClientSelectedIds([]);
        setCleanNodes(true);
    }

    const handleAddClientParentAccess = () => {
        clientSelectedIds.map(async (parentClientId) => {
            const updatedState = getUpdatedStateOnParentSelection(parentClientId);
            const clientIds = updatedState.selectedClientsForMappedTreeview.filter(item => !(item in parentsClientsIndexed.Parents));
            
            await addClientParentAccess({
                variables: {
                    input: {
                        EmployeeId: employeeId,
                        ClientsSelected: clientIds.map(
                            (client) => ({
                                ClientCode: client,
                            }),
                        )
                    }
                }
            });

            return setParentClientState({
                ...parentClientState,
                ...updatedState
            });
        });
        handleClear();
    }

    const handleRemoveClientParentAccess = () => {
        clientSelectedIds.map(async (parentClientId) => {
            const updatedState = getUpdatedStateOnParentRemoval(parentClientId);
            const clientIds = updatedState.clientsToRemove.filter(item => !(item in parentsClientsIndexed.Parents));

            await removeClientParentAccess({
                variables: {
                    input: {
                        ClientsSelected: clientIds.map(
                            (client) => ({
                                ClientCode: client,
                            }),
                        )
                    }
                },
            });

            const clientFiltered = clientSelectedIds.filter(client => client !== parentClientId)
            setClientSelectedIds(clientFiltered);
            return setParentClientState({ ...parentClientState, ...updatedState })
        });
        handleClear();
    }

    const handleSelectNode = (nodeIds) => {
        setSelectedClient(true)
        setClientSelectedIds(nodeIds);
        setCleanNodes(false);
    }
    const handleRemoveNode = (nodeIds) => {
        setSelectedClient(false)
        setClientSelectedIds(nodeIds);
        setCleanNodes(false);
    }

    const handleClickSave = () => {
        if (selectedClient) {
            handleAddClientParentAccess()
        }
        else {
            handleRemoveClientParentAccess()
        }
    }

    return (
        <Box className='administration__container'>
            <Box className='administration__details__wrapper'>
                <p className='administration__details__title'>
                    {CPPSTitle}
                </p>
                <Box className='admin__details__box__wrapper'>
                    <Box className='admin__details__box search-clients'>
                        <CustomTextField
                            width='390px'
                            title='Client Search'
                            value={search.searchClients}
                            handleChange={handleSearchClientsSearch}
                            placeholder='Search'
                            inputIcon
                        />
                        <Box className='client-search-treeview-box'>
                            <ControlledTreeView
                                isLoading={addAccessIsLoading || searchClientsOnMount || refetchClientsOnMount || dataClientParentAccessIsLoading || false}
                                item={parentWithChildrenInput.searchClients}
                                selectedItems={[]}
                                handleSelection={handleSelectNode}
                                allExpanded
                                childless
                                cleanNodes={cleanNodes}
                            />
                        </Box>
                    </Box>
                    <Box className='admin__details__box selected'>
                        <CustomTextField
                            width='390px'
                            title='Selected Clients'
                            value={search.selectedClients}
                            handleChange={handleMappedSearch}
                            placeholder='Search'
                            inputIcon
                        />
                        <Box className='selected-clients-treeview-box'>
                            <ControlledTreeView
                                isLoading={removeAccessIsLoading || selectedClientsOnMount || searchClientsOnMount || addAccessIsLoading || refetchClientsOnMount || dataClientParentAccessIsLoading || false}
                                item={parentWithChildrenInput.selectedClients}
                                selectedItems={parentClientState.selectedClientsForMappedTreeview || []}
                                handleSelection={handleRemoveNode}
                                allExpanded
                                childless
                                cleanNodes={cleanNodes}
                            />
                        </Box>
                    </Box>
                </Box>
            </Box>
            <Box className='administration__footer'>
            <CustomButtons
                handleClick={handleClear}
                name='Clear'
            />
            <CustomButtons
                handleClick={handleClickSave}
                name='Save'
                disabled={!clientSelectedIds.length}
                tooltipFree
                loadingButton={addAccessIsLoading}
                loading={addAccessIsLoading || removeAccessIsLoading}
            />
            </Box>
            <ToastContainer hideProgressBar />
        </Box>
    )
}

export default ClientManagement